import { useState } from 'react';
import { useSearchParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MailCheck } from 'lucide-react';

import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout';
import { Alert, Button } from '@agion/ui';
import { config } from '@/utils/config';

export function EmailVerificationPendingPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      // The session selects the pending verification and stored recipient.
      // The email query parameter is display text, never resend authority.
      const response = await fetch(`${config.apiUrl}/api/v1/domain-verification/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Send session cookie for authentication
        body: '{}',
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t('errors.sessionExpired'));
        }
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to resend verification email');
      }

      setResendSuccess(true);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthSplitLayout>
      <div className="lp-state lp-pop">
        <div className="lp-badge lp-badge-success">
          <MailCheck size={24} aria-hidden="true" />
        </div>
        <h2 className="lp-state-title">{t('emailVerification.title')}</h2>
        <p className="lp-state-msg">
          {email
            ? t('emailVerification.descriptionWithEmail', { email })
            : t('emailVerification.description')}
        </p>
        <p className="lp-note">{t('emailVerification.expiryWarning')}</p>
        <p className="lp-note">{t('emailVerification.checkSpam')}</p>
        <hr className="lp-state-divider" />

        {resendSuccess && (
          <Alert variant="success" className="lp-alert">
            {t('emailVerification.resendSuccess')}
          </Alert>
        )}

        {resendError && (
          <Alert variant="error" className="lp-alert">
            {resendError}
          </Alert>
        )}

        <p className="lp-state-prompt">{t('emailVerification.didntReceive')}</p>
        <Button
          onClick={handleResendEmail}
          variant="outline"
          className="lp-btn-social"
          disabled={isResending}
          isLoading={isResending}
        >
          {t('emailVerification.resendButton')}
        </Button>

        <Link to="/login" className="lp-back">
          <ArrowLeft size={16} aria-hidden="true" />
          {t('backToLogin')}
        </Link>
      </div>
    </AuthSplitLayout>
  );
}

export default EmailVerificationPendingPage;
